/* global window console navigator */
import * as React from "react";
import PropType from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { insert, resize, updateCell } from "../../state/structureSlice";
import { onSelectionByCoordinates } from "../../helpers/selection";
import { notify } from "./GlobalToast";
import { tryParseClipboard, ContentType, updateOnBox } from "../../helpers/copyPaste";
import { openCopyPasteDialog } from "../../state/appStateSlice";
import { resetOne, updateShapeGroup } from "../../state/cellStylesSlice";
import { batchGroupBy } from "../../helpers/batchGroupBy";
import { v4 as uuidv4 } from "uuid";

const ClipboardContext = (props) => {
  const dispatch = useDispatch();
  const state = useSelector((state) => state);

  const prepareGriddData = (contentOverride) => {
    const data = {
      selection: state.selection,
    };

    if (state.selection.table) {
      data.cells = state.gridd.present.structure.cells;
      data.cellStyles = state.gridd.present.cellStyles;
      data.columns = state.gridd.present.structure.columns;
      data.rows = state.gridd.present.structure.rows;
      data.spacerStyles = state.gridd.present.spacerStyles;
    } else if (state.selection.cells.length > 0) {
      data.cells = state.gridd.present.structure.cells
        .map((row) => {
          return row.filter((v) => state.selection.cells.find((s) => s.id === v.id));
        })
        .reduce((a, n) => [...a, ...n], []);

      // for first cell, content override must be added otherwise unblurred cell does not do dispatch in time
      if (contentOverride?.id === data.cells[0].id) {
        const newCell = { ...data.cells[0] };
        newCell.text = contentOverride.text ?? data.cells[0].text;
        newCell.json = contentOverride.json ?? data.cells[0].json;
        data.cells[0] = newCell;
      }

      data.cellStyles = {};
      Object.keys(state.gridd.present.cellStyles)
        .filter((key) => data.cells.find((c) => c.id === key))
        .forEach((key) => (data.cellStyles[key] = state.gridd.present.cellStyles[key]));
      data.shapeGroups = {};
      Object.keys(data.cellStyles)
        .map((key) => data.cellStyles[key].shapeGroupId)
        .filter(Boolean)
        .forEach((id) => (data.shapeGroups[id] = state.gridd.present.cellStyles.shapeGroups[id]));
    } else if (state.selection.columns.length > 0) {
      data.cells = state.gridd.present.structure.cells.map((row) => {
        return row.filter((v, i) => state.selection.columns.includes(i));
      });
      const cellsReduced = data.cells.reduce((a, n) => [...a, ...n], []);
      data.cellStyles = {};
      Object.keys(state.gridd.present.cellStyles)
        .filter((key) => cellsReduced.find((c) => c.id === key))
        .forEach((key) => (data.cellStyles[key] = state.gridd.present.cellStyles[key]));
      data.shapeGroups = {};
      Object.keys(data.cellStyles)
        .map((key) => data.cellStyles[key].shapeGroupId)
        .filter(Boolean)
        .forEach((id) => (data.shapeGroups[id] = state.gridd.present.cellStyles.shapeGroups[id]));

      data.columns = state.gridd.present.structure.columns.filter((c, i) => state.selection.columns.includes(i));
    } else if (state.selection.rows.length > 0) {
      data.cells = state.gridd.present.structure.cells.filter((r, ri) => state.selection.rows.includes(ri));
      const cellsReduced = data.cells.reduce((a, n) => [...a, ...n], []);
      data.cellStyles = {};
      Object.keys(state.gridd.present.cellStyles)
        .filter((key) => cellsReduced.find((c) => c.id === key))
        .forEach((key) => (data.cellStyles[key] = state.gridd.present.cellStyles[key]));
      data.shapeGroups = {};
      Object.keys(data.cellStyles)
        .map((key) => data.cellStyles[key].shapeGroupId)
        .filter(Boolean)
        .forEach((id) => (data.shapeGroups[id] = state.gridd.present.cellStyles.shapeGroups[id]));
      data.rows = state.gridd.present.structure.rows.filter((c, i) => state.selection.rows.includes(i));
    }

    return data;
  };

  const onCopy = (e, contentOverride) => {
    console.log("on copy", { e, state, contentOverride });
    if (
      state.selection.cells.length > 0 ||
      state.selection.rows.length > 0 ||
      state.selection.columns.length > 0 ||
      state.selection.table
    ) {
      const data = prepareGriddData(contentOverride);
      console.log("on Gridd copy", { data, state, contentOverride });
      const stringified = JSON.stringify(data);
      navigator.clipboard.writeText(stringified);
      notify("success", "success.copy.cell");
    }
    // if (state.selection.cells.length > 0) {
    //   const text =
    //     textOverride ??
    //     state.gridd.present.structure.cells[state.selection.cells[0].x][state.selection.cells[0].y].text;
    //   navigator.clipboard.writeText(text);
    //   console.log("copied cell", text);
    //   notify("success", "success.copy.cell");
    // }
  };

  const onParsed = (parsed) => {
    console.log("parsed clipboard", parsed);
    if (parsed.type === ContentType.Text) {
      onSelectionByCoordinates(dispatch, updateCell, state, { text: parsed.content });
      notify("success", "success.paste.cell");
    } else if (parsed.type === ContentType.Table) {
      if (parsed.content.length * parsed.content[0].length > 200) {
        notify("warning", "warning.paste.large");
        const truncated = {
          type: ContentType.Table,
          content: parsed.content.slice(0, 10).map((row) => row.slice(0, 20)),
        };
        dispatch(openCopyPasteDialog(truncated));
      } else {
        const rowLength = state.gridd.present.structure.rows.filter((r) => r[0] !== "s").length;
        const colLength = state.gridd.present.structure.columns.filter((c) => c[0] !== "s").length;
        console.log("Determining expansion need", {
          rowNeeds: parsed.content.length,
          colNeeds: parsed.content[0].length,
          rowLength,
          colLength,
          selectedCell: state.selection.cells[0],
        });
        if (
          parsed.content.length > rowLength - (state.selection.cells[0].x ?? 0) ||
          parsed.content[0].length > colLength - (state.selection.cells[0].y ?? 0)
        ) {
          dispatch(openCopyPasteDialog(parsed));
        } else {
          updateOnBox(
            dispatch,
            updateCell,
            state,
            parsed.content,
            state.selection.cells[0] ??
              (state.selection.table
                ? { x: 0, y: 0 }
                : { x: state.selection.rows[0] ?? 0, y: state.selection.columns[0] ?? 0 })
          );
        }
      }
    } else if (parsed.type === ContentType.Gridd) {
      console.log("Parsed Gridd copy data", { parsed, state });
      const { selection } = state;
      const structure = state.gridd.present.structure;
      batchGroupBy.start();
      if (
        parsed.content.selection.table ||
        parsed.content.selection.columns.length > 0 ||
        parsed.content.selection.rows.length > 0
      ) {
        const { x: t, y: l } = selection.cells.length
          ? selection.cells[0]
          : {
              x: selection.rows.length ? selection.rows[0] : 0,
              y: selection.columns.length ? selection.columns[0] : 0,
            };
        let colCount = structure.columns.length;
        let rowCount = structure.rows.length;
        let cellIds = structure.cells.map((row) => row.map((cell) => cell.id));
        for (let i = 0; i < parsed.content.columns?.length; i++) {
          const weight = parsed.content.columns[i];
          const place = i + l;
          if (cellIds[0].length - 1 < place) {
            colCount++;
            const newCellIds = [];
            for (let k = 0; k < rowCount; k++) {
              newCellIds.push(uuidv4());
            }
            cellIds = cellIds.map((row, ri) => [...row, newCellIds[ri]]);
            dispatch(insert({ newCellIds, columnIndex: place }));
            // console.warn("Inserted column", {
            //   cellIds,
            //   place,
            //   l,
            //   i,
            //   availableCols: structure.columns.filter((c) => c[0] !== "s").length,
            // });
          }
          dispatch(resize({ weight, columnIndex: place }));
        }

        for (let i = 0; i < parsed.content.rows?.length; i++) {
          const weight = parsed.content.rows[i];
          const place = i + t;
          if (cellIds.length - 1 < place) {
            rowCount++;
            const newCellIds = [];
            for (let k = 0; k < colCount; k++) {
              newCellIds.push(uuidv4());
            }
            cellIds.push(newCellIds);
            dispatch(insert({ newCellIds, rowIndex: place }));
            console.warn("Inserted row", cellIds);
          }
          dispatch(resize({ weight, rowIndex: place }));
        }

        console.log("Parsing cells", { t, l, parsed, state, cellIds });
        const groupMap = {};
        for (let i = 0; i < parsed.content.cells.length; i++) {
          const row = parsed.content.cells[i];
          for (let j = 0; j < row.length; j++) {
            const x = t + i;
            const y = l + j;
            const cell = row[j];
            const cellStyle = parsed.content.cellStyles[cell.id];
            const updateCellPayload = { x, y, text: cell.text ?? "", json: cell.json };
            const updateStylePayload = {
              ...cellStyle,
              id: cellIds.length > x && cellIds[0].length > y ? cellIds[x][y] : "failed",
            };
            if (updateStylePayload.shapeGroupId) {
              if (!groupMap[updateStylePayload.shapeGroupId]) {
                const newGroupId = uuidv4();
                groupMap[updateStylePayload.shapeGroupId] = newGroupId;
                updateStylePayload.shapeGroupId = newGroupId;
              } else {
                updateStylePayload.shapeGroupId = groupMap[updateStylePayload.shapeGroupId];
              }
            }
            console.log("Updating table cell from clipboard", { x, y, cellIds, updateCellPayload, updateStylePayload });
            dispatch(updateCell(updateCellPayload));
            dispatch(resetOne(updateStylePayload));
          }
        }

        Object.keys(parsed.content.shapeGroups ?? {}).forEach((key) => {
          dispatch(updateShapeGroup({ id: groupMap[key], ...parsed.content.shapeGroups[key] }));
        });
      } else if (parsed.content.cells.length > 0) {
        const groupMap = {};
        for (let i = 0; i < state.selection.cells.length; i++) {
          const { x, y, id } = state.selection.cells[i];
          const cell = parsed.content.cells[i];
          const cellStyle = parsed.content.cellStyles[cell.id];
          const updateCellPayload = { x, y, text: cell.text, json: cell.json };
          const updateStylePayload = { ...cellStyle, id };
          if (updateStylePayload.shapeGroupId) {
            if (!groupMap[updateStylePayload.shapeGroupId]) {
              const newGroupId = uuidv4();
              groupMap[updateStylePayload.shapeGroupId] = newGroupId;
              updateStylePayload.shapeGroupId = newGroupId;
            } else {
              updateStylePayload.shapeGroupId = groupMap[updateStylePayload.shapeGroupId];
            }
          }
          console.log("Updating cell from clipboard", { updateCellPayload, updateStylePayload });
          dispatch(updateCell(updateCellPayload));
          dispatch(resetOne(updateStylePayload));
        }

        Object.keys(parsed.content.shapeGroups ?? {}).forEach((key) => {
          dispatch(updateShapeGroup({ id: groupMap[key], ...parsed.content.shapeGroups[key] }));
        });
      } else {
        notify("warning", "warning.paste.selection");
      }
      batchGroupBy.end();
    }
  };

  const onPaste = async (e) => {
    console.log("on paste", { e, state });
    try {
      const parsed = await tryParseClipboard();
      if (parsed?.type) {
        onParsed(parsed);
      } else {
        notify("warning", "warning.paste.cell");
      }
    } catch (error) {
      console.error("Clipboard read failed", error);
      notify("error", "error.paste.clipboard");
    }
  };

  const onPastePreview = async (e) => {
    console.log("on paste preview", { e, state });
    try {
      const textContent = await navigator.clipboard.readText();
      const content = await navigator.clipboard.read();
      const contentItems = [];
      for (const item of content) {
        const itemTypes = item.types;
        const typedContent = [];
        for (const type of itemTypes) {
          const blob = await item.getType(type);
          if (blob) {
            const itemContent = await blob.text();
            typedContent.push({ type, itemContent });
          }
        }
        contentItems.push({ itemTypes, typedContent });
      }
      console.log("on paste preview items parsed", { textContent, contentItems });
    } catch (error) {
      console.error("Clipboard item read failed", error);
    }
  };

  window.clipboardWithContext = { onCopy, onPaste, onParsed, onPastePreview, tryParseClipboard };

  return <>{props.children}</>;
};

ClipboardContext.propTypes = {
  children: PropType.element.isRequired,
};

export default ClipboardContext;
